import React, { useState, useEffect, useRef } from "react";
import {
  Users,
  Shield,
  Mail,
  Key,
  Edit,
  Trash2,
  Plus,
  Search,
  Filter,
  MoreVertical,
  UserCheck,
  UserX,
  ChevronDown,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
} from "lucide-react";
import { useUser } from "../contexts/UserContext";
import { useNotification } from "../contexts/NotificationContext";
import { userAPI } from "../utils/api";
import Avatar from "./Avatar";
import ConfirmationModal from "./ConfirmationModal";
import {
  validateField,
  validatePasswordStrength,
  validateUserForm,
  validatePasswordResetForm,
  parseAPIError,
} from "../utils/validationUtils";

const UserManagement = () => {
  const { user: currentUser } = useUser();
  const { showToast } = useNotification();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("active"); // "active" | "inactive"
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToActivate, setUserToActivate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRoleFilterOpen, setIsRoleFilterOpen] = useState(false);
  const [isAddRoleDropdownOpen, setIsAddRoleDropdownOpen] = useState(false);
  const addUserModalRef = useRef(null);
  const editUserModalRef = useRef(null);
  const resetPasswordModalRef = useRef(null);
  const roleFilterDropdownRef = useRef(null);
  const addUserRoleDropdownRef = useRef(null);

  // Form states
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    role: "member",
    password: "",
  });

  const [userFormErrors, setUserFormErrors] = useState({});
  const [passwordStrengthFeedback, setPasswordStrengthFeedback] = useState(null);

  const [resetPasswordForm, setResetPasswordForm] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [resetPasswordErrors, setResetPasswordErrors] = useState({});
  const [resetPasswordStrengthFeedback, setResetPasswordStrengthFeedback] = useState(null);

  // Password visibility states
  const [showAddUserPassword, setShowAddUserPassword] = useState(false);
  const [showResetNewPassword, setShowResetNewPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter, activeTab]);

  // Handle click outside to close add user modal
  useEffect(() => {
    if (!showAddUser) return;

    const handleClickOutside = (event) => {
      if (
        addUserModalRef.current &&
        !addUserModalRef.current.contains(event.target)
      ) {
        handleCloseAddUserModal();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAddUser]);

  // Handle click outside to close edit user modal
  useEffect(() => {
    if (!showEditUser) return;

    const handleClickOutside = (event) => {
      if (
        editUserModalRef.current &&
        !editUserModalRef.current.contains(event.target)
      ) {
        handleCloseEditUserModal();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEditUser]);

  // Handle click outside to close reset password modal
  useEffect(() => {
    if (!showResetPassword) return;

    const handleClickOutside = (event) => {
      if (
        resetPasswordModalRef.current &&
        !resetPasswordModalRef.current.contains(event.target)
      ) {
        handleCloseResetPasswordModal();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showResetPassword]);

  // Close add-user role dropdown when clicking outside
  useEffect(() => {
    if (!isAddRoleDropdownOpen) return;

    const handleClickOutside = (event) => {
      if (
        addUserRoleDropdownRef.current &&
        !addUserRoleDropdownRef.current.contains(event.target)
      ) {
        setIsAddRoleDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isAddRoleDropdownOpen]);

  // Close role filter dropdown when clicking outside
  useEffect(() => {
    if (!isRoleFilterOpen) return;

    const handleClickOutside = (event) => {
      if (
        roleFilterDropdownRef.current &&
        !roleFilterDropdownRef.current.contains(event.target)
      ) {
        setIsRoleFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isRoleFilterOpen]);

  // Check if user is admin
  if (!currentUser || currentUser.role !== "admin") {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600 dark:text-slate-400">
            You need admin privileges to access this page.
          </p>
        </div>
      </div>
    );
  }

  const fetchUsers = async () => {
    try {
      const response = await userAPI.getUsers();
      const sortedUsers = (response.data.users || []).sort((a, b) => {
        const aActive = a.isActive !== false;
        const bActive = b.isActive !== false;
        if (aActive !== bActive) {
          return aActive ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      });
      setUsers(sortedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      const parsedError = parseAPIError(error);
      showToast(parsedError.message, "error");
    }
  };

  const handleCloseAddUserModal = () => {
    setShowAddUser(false);
    setShowAddUserPassword(false);
    setUserForm({ name: "", email: "", role: "member", password: "" });
    setUserFormErrors({});
    setPasswordStrengthFeedback(null);
  };

  const handleCloseEditUserModal = () => {
    setShowEditUser(false);
    setSelectedUser(null);
    setUserForm({ name: "", email: "", role: "member", password: "" });
    setUserFormErrors({});
  };

  const handleCloseResetPasswordModal = () => {
    setShowResetPassword(false);
    setShowResetNewPassword(false);
    setShowResetConfirmPassword(false);
    setSelectedUser(null);
    setResetPasswordForm({ newPassword: "", confirmPassword: "" });
    setResetPasswordErrors({});
    setResetPasswordStrengthFeedback(null);
  };

  // Real-time field validation for user form
  const handleUserFormFieldChange = (fieldName, value) => {
    setUserForm({ ...userForm, [fieldName]: value });

    // Real-time validation
    if (fieldName === "password") {
      if (value) {
        const strengthFeedback = validatePasswordStrength(value);
        setPasswordStrengthFeedback(strengthFeedback);
      } else {
        setPasswordStrengthFeedback(null);
      }
    }

    // Clear error for this field when user starts typing
    if (userFormErrors[fieldName]) {
      const error = validateField(fieldName, value);
      if (!error) {
        setUserFormErrors({
          ...userFormErrors,
          [fieldName]: undefined,
        });
      } else {
        setUserFormErrors({
          ...userFormErrors,
          [fieldName]: error,
        });
      }
    }
  };

  // Real-time field validation for reset password form
  const handleResetPasswordFieldChange = (fieldName, value) => {
    setResetPasswordForm({ ...resetPasswordForm, [fieldName]: value });

    if (fieldName === "newPassword") {
      if (value) {
        const strengthFeedback = validatePasswordStrength(value);
        setResetPasswordStrengthFeedback(strengthFeedback);
      } else {
        setResetPasswordStrengthFeedback(null);
      }
    }

    // Clear error for this field when user starts typing
    if (resetPasswordErrors[fieldName]) {
      setResetPasswordErrors({
        ...resetPasswordErrors,
        [fieldName]: undefined,
      });
    }
  };

  const filterUsers = () => {
    let filtered = users.filter((user) => {
      const isUserActive = user.isActive !== false;
      const matchesTab = activeTab === "active" ? isUserActive : !isUserActive;
      const matchesSearch =
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter === "all" || user.role === roleFilter;
      return matchesTab && matchesSearch && matchesRole;
    });

    filtered.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });

    setFilteredUsers(filtered);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate form before submission
    const validation = validateUserForm(userForm, { validatePassword: true });
    if (!validation.isValid) {
      setUserFormErrors(validation.errors);
      showToast("Please fix validation errors before submitting", "error");
      setLoading(false);
      return;
    }

    try {
      // Only send required fields
      const { name, email, role, password } = userForm;
      const response = await userAPI.createUser({
        name,
        email,
        role,
        password,
      });

      showToast("User created successfully", "success");
      handleCloseAddUserModal();
      fetchUsers();
    } catch (error) {
      console.error("Error creating user:", error);
      const parsedError = parseAPIError(error);

      // Handle specific validation errors from API
      if (parsedError.code === "VALIDATION_ERROR" && parsedError.details) {
        const apiErrors = {};
        Object.keys(parsedError.details).forEach((field) => {
          apiErrors[field] = parsedError.details[field];
        });
        setUserFormErrors(apiErrors);
      }

      showToast(parsedError.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate form (without password for edit)
    const validation = validateUserForm(userForm, { validatePassword: false });
    if (!validation.isValid) {
      setUserFormErrors(validation.errors);
      showToast("Please fix validation errors before submitting", "error");
      setLoading(false);
      return;
    }

    try {
      // Only send name, email, and role - no password
      const { name, email, role } = userForm;
      const response = await userAPI.updateUser(
        selectedUser._id || selectedUser.id,
        { name, email, role }
      );

      showToast("User updated successfully", "success");
      handleCloseEditUserModal();
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      const parsedError = parseAPIError(error);

      // Handle specific validation errors from API
      if (parsedError.code === "VALIDATION_ERROR" && parsedError.details) {
        const apiErrors = {};
        Object.keys(parsedError.details).forEach((field) => {
          apiErrors[field] = parsedError.details[field];
        });
        setUserFormErrors(apiErrors);
      }

      showToast(parsedError.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate reset password form
    const validation = validatePasswordResetForm(resetPasswordForm);
    if (!validation.isValid) {
      setResetPasswordErrors(validation.errors);
      showToast("Please fix validation errors before submitting", "error");
      setLoading(false);
      return;
    }

    try {
      const response = await userAPI.resetUserPassword(
        selectedUser._id || selectedUser.id,
        resetPasswordForm.newPassword
      );

      showToast("Password reset successfully", "success");
      handleCloseResetPasswordModal();
    } catch (error) {
      console.error("Error resetting password:", error);
      const parsedError = parseAPIError(error);

      // Handle specific validation errors from API
      if (parsedError.code === "VALIDATION_ERROR" && parsedError.details) {
        const apiErrors = {};
        Object.keys(parsedError.details).forEach((field) => {
          apiErrors[field] = parsedError.details[field];
        });
        setResetPasswordErrors(apiErrors);
      }

      showToast(parsedError.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (user) => {
    if (user._id === (currentUser._id || currentUser.id)) {
      showToast("Cannot delete your own account", "error");
      return;
    }

    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setLoading(true);
      await userAPI.deleteUser(userToDelete._id || userToDelete.id);

      showToast("User deactivated successfully", "success");
      fetchUsers();
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    } catch (error) {
      console.error("Error deleting user:", error);
      const parsedError = parseAPIError(error);
      showToast(parsedError.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleActivateUser = (user) => {
    setUserToActivate(user);
    setShowActivateConfirm(true);
  };

  const confirmActivateUser = async () => {
    if (!userToActivate) return;

    try {
      setLoading(true);
      await userAPI.activateUser(userToActivate._id || userToActivate.id);
      showToast(`User ${userToActivate.name} activated successfully`, "success");
      fetchUsers();
      setShowActivateConfirm(false);
      setUserToActivate(null);
    } catch (error) {
      console.error("Error activating user:", error);
      const parsedError = parseAPIError(error);
      showToast(parsedError.message || "Failed to activate user", "error");
    } finally {
      setLoading(false);
    }
  };

  const openEditUser = (user) => {
    setSelectedUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      role: user.role,
      password: "",
    });
    setUserFormErrors({});
    setShowEditUser(true);
  };

  const openResetPassword = (user) => {
    setSelectedUser(user);
    setResetPasswordForm({ newPassword: "", confirmPassword: "" });
    setResetPasswordErrors({});
    setResetPasswordStrengthFeedback(null);
    setShowResetNewPassword(false);
    setShowResetConfirmPassword(false);
    setShowResetPassword(true);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      case "member":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300";
    }
  };

  const getRoleIcon = (role) => {
    return role === "admin" ? (
      <Shield className="w-4 h-4" />
    ) : (
      <Users className="w-4 h-4" />
    );
  };

  // Component for displaying field errors
  const FieldError = ({ error }) => {
    if (!error) return null;
    return (
      <div className="mt-1 flex items-start space-x-2">
        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  };

  // Component for password strength feedback
  const PasswordStrengthFeedback = ({ feedback }) => {
    if (!feedback) return null;

    const getStrengthColor = (strength) => {
      switch (strength) {
        case "strong":
          return "text-green-600 dark:text-green-400";
        case "moderate":
          return "text-yellow-600 dark:text-yellow-400";
        case "weak":
          return "text-red-600 dark:text-red-400";
        default:
          return "text-gray-600 dark:text-slate-400";
      }
    };

    const getStrengthIcon = (strength) => {
      switch (strength) {
        case "strong":
          return <CheckCircle className="w-4 h-4" />;
        case "moderate":
        case "weak":
          return <AlertCircle className="w-4 h-4" />;
        default:
          return null;
      }
    };

    return (
      <div className={`mt-2 flex items-start space-x-2 ${getStrengthColor(feedback.strength)}`}>
        {getStrengthIcon(feedback.strength)}
        <p className="text-sm">{feedback.message}</p>
      </div>
    );
  };

  const activeUsersCount = users.filter((u) => u.isActive !== false).length;
  const inactiveUsersCount = users.filter((u) => u.isActive === false).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-5 py-4 mb-4 flex-shrink-0 transition-colors duration-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-1">
              <h1 className="text-base font-semibold dark:text-white transition-colors duration-200">User Management</h1>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400">
                Total Users: {users.length}
              </span>
            </div>
            <p className="text-md dark:text-slate-400 transition-colors duration-200">
              Manage users and their access to the system
            </p>
          </div>
          <button
            onClick={() => setShowAddUser(true)}
            className="border-blue-200 dark:border-blue-800 border text-blue-600 dark:text-blue-400 px-4 py-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-200 flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add User</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center space-x-2 border-b border-gray-200 dark:border-slate-700 pb-0">
        <button
          type="button"
          onClick={() => setActiveTab("active")}
          className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all duration-200 ${
            activeTab === "active"
              ? "border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 font-semibold"
              : "border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:border-gray-300 dark:hover:border-slate-600"
          }`}
        >
          <UserCheck className="w-4 h-4" />
          <span>Active</span>
          <span
            className={`ml-1 px-2 py-0.5 text-xs rounded-full font-medium ${
              activeTab === "active"
                ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                : "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300"
            }`}
          >
            {activeUsersCount}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("inactive")}
          className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all duration-200 ${
            activeTab === "inactive"
              ? "border-red-500 text-red-600 dark:text-red-400 dark:border-red-400 font-semibold"
              : "border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:border-gray-300 dark:border-slate-600"
          }`}
        >
          <UserX className="w-4 h-4" />
          <span>Inactive</span>
          <span
            className={`ml-1 px-2 py-0.5 text-xs rounded-full font-medium ${
              activeTab === "inactive"
                ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                : "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300"
            }`}
          >
            {inactiveUsersCount}
          </span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-slate-700 transition-colors duration-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
              />
            </div>
          </div>
          <div className="sm:w-48" ref={roleFilterDropdownRef}>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsRoleFilterOpen((prev) => !prev)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between bg-white dark:bg-slate-900 text-black dark:text-white transition-colors duration-200"
                aria-haspopup="listbox"
                aria-expanded={isRoleFilterOpen}
              >
                <span className="capitalize">
                  {roleFilter === "all"
                    ? "All Roles"
                    : roleFilter === "admin"
                    ? "Admin"
                    : "Member"}
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                    isRoleFilterOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {isRoleFilterOpen && (
                <div className="absolute mt-1 w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg z-30 overflow-hidden transition-colors duration-200">
                  <button
                    type="button"
                    onClick={() => {
                      setRoleFilter("all");
                      setIsRoleFilterOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors ${
                      roleFilter === "all" ? "bg-gray-50 dark:bg-slate-700/50 font-semibold" : "dark:text-slate-200"
                    }`}
                    role="option"
                    aria-selected={roleFilter === "all"}
                  >
                    All Roles
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setRoleFilter("admin");
                      setIsRoleFilterOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors ${
                      roleFilter === "admin" ? "bg-gray-50 dark:bg-slate-700/50 font-semibold" : "dark:text-slate-200"
                    }`}
                    role="option"
                    aria-selected={roleFilter === "admin"}
                  >
                    Admin
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setRoleFilter("member");
                      setIsRoleFilterOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors ${
                      roleFilter === "member" ? "bg-gray-50 dark:bg-slate-700/50 font-semibold" : "dark:text-slate-200"
                    }`}
                    role="option"
                    aria-selected={roleFilter === "member"}
                  >
                    Member
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden transition-colors duration-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
            <thead className="bg-gray-50 dark:bg-slate-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700 transition-colors duration-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500 dark:text-slate-400">
                    <div className="flex flex-col items-center justify-center space-y-2">
                      {activeTab === "active" ? (
                        <UserCheck className="w-8 h-8 text-gray-400 dark:text-slate-500" />
                      ) : (
                        <UserX className="w-8 h-8 text-gray-400 dark:text-slate-500" />
                      )}
                      <p className="text-sm font-medium">
                        {searchTerm || roleFilter !== "all"
                          ? `No ${activeTab === "active" ? "active" : "inactive"} users match your filters.`
                          : `No ${activeTab === "active" ? "active" : "inactive"} users found.`}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user._id || user.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Avatar user={user} size="md" showTooltip={true} />
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-slate-400">
                            ID: {user._id || user.id}
                          </div>
                        </div>
                      </div>
                    </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(
                        user.role
                      )}`}
                    >
                      {getRoleIcon(user.role)}
                      <span className="ml-1 capitalize">{user.role}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-slate-300">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.isActive !== false ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                        <UserCheck className="w-3 h-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400">
                        <UserX className="w-3 h-3 mr-1" />
                        Deactivated
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {user.isActive === false ? (
                        <button
                          onClick={() => handleActivateUser(user)}
                          className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 p-1 rounded hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                          title="Activate User"
                        >
                          <UserCheck className="w-4 h-4" />
                        </button>
                      ) : (
                        (user._id || user.id) !==
                          (currentUser._id || currentUser.id) && (
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            title="Deactivate User"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )
                      )}
                      <button
                        onClick={() => openEditUser(user)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                        title="Edit User"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openResetPassword(user)}
                        className="text-orange-600 dark:text-orange-400 hover:text-orange-900 dark:hover:text-orange-300 p-1 rounded hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                        title="Reset Password"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
          <div
            ref={addUserModalRef}
            className="bg-white dark:bg-slate-800 rounded-lg w-full max-w-lg shadow-xl border dark:border-slate-700 transition-colors duration-200"
          >
            <div className="bg-[#f1f2f4] dark:bg-slate-900 flex items-center justify-between p-4 border-b dark:border-slate-700 rounded-tl-lg rounded-tr-lg transition-colors duration-200">
              <div>
                <h2 className="text-2xl font-bold text-[#222785] dark:text-slate-200">
                  Add New User
                </h2>
              </div>
            </div>
            <form onSubmit={handleAddUser} className="space-y-4 p-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={userForm.name}
                  onChange={(e) =>
                    handleUserFormFieldChange("name", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition-colors duration-200 bg-white dark:bg-slate-900 text-black dark:text-white ${
                    userFormErrors.name
                      ? "border-red-500 dark:border-red-500 focus:ring-red-500"
                      : "border-gray-300 dark:border-slate-600 focus:ring-blue-500"
                  }`}
                  placeholder="Enter user name"
                />
                <FieldError error={userFormErrors.name} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) =>
                    handleUserFormFieldChange("email", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition-colors duration-200 bg-white dark:bg-slate-900 text-black dark:text-white ${
                    userFormErrors.email
                      ? "border-red-500 dark:border-red-500 focus:ring-red-500"
                      : "border-gray-300 dark:border-slate-600 focus:ring-blue-500"
                  }`}
                  placeholder="Enter email address"
                />
                <FieldError error={userFormErrors.email} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Role
                </label>
                <div className="relative" ref={addUserRoleDropdownRef}>
                  <button
                    type="button"
                    onClick={() =>
                      setIsAddRoleDropdownOpen((prevState) => !prevState)
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent flex items-center justify-between transition-colors duration-200 bg-white dark:bg-slate-900 text-black dark:text-white ${
                      userFormErrors.role
                        ? "border-red-500 dark:border-red-500 focus:ring-red-500"
                        : "border-gray-300 dark:border-slate-600 focus:ring-blue-500"
                    }`}
                    aria-haspopup="listbox"
                    aria-expanded={isAddRoleDropdownOpen}
                  >
                    <span className="capitalize">
                      {userForm.role === "member" ? "Member" : "Admin"}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                        isAddRoleDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isAddRoleDropdownOpen && (
                    <div className="absolute mt-1 w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg z-30 overflow-hidden transition-colors duration-200">
                      <button
                        type="button"
                        onClick={() => {
                          handleUserFormFieldChange("role", "member");
                          setIsAddRoleDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors ${
                          userForm.role === "member"
                            ? "bg-gray-50 dark:bg-slate-700/50 font-semibold"
                            : "dark:text-slate-200"
                        }`}
                        role="option"
                        aria-selected={userForm.role === "member"}
                      >
                        Member
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          handleUserFormFieldChange("role", "admin");
                          setIsAddRoleDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors ${
                          userForm.role === "admin"
                            ? "bg-gray-50 dark:bg-slate-700/50 font-semibold"
                            : "dark:text-slate-200"
                        }`}
                        role="option"
                        aria-selected={userForm.role === "admin"}
                      >
                        Admin
                      </button>
                    </div>
                  )}
                </div>
                <FieldError error={userFormErrors.role} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showAddUserPassword ? "text" : "password"}
                    value={userForm.password}
                    onChange={(e) =>
                      handleUserFormFieldChange("password", e.target.value)
                    }
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:border-transparent transition-colors duration-200 bg-white dark:bg-slate-900 text-black dark:text-white ${
                      userFormErrors.password
                        ? "border-red-500 dark:border-red-500 focus:ring-red-500"
                        : "border-gray-300 dark:border-slate-600 focus:ring-blue-500"
                    }`}
                    placeholder="Enter password (min. 8 characters)"
                  />
                  <button
                    type="button"
                    tabIndex="-1"
                    onClick={() => setShowAddUserPassword(!showAddUserPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 transition-colors"
                    title={showAddUserPassword ? "Hide password" : "Show password"}
                  >
                    {showAddUserPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <FieldError error={userFormErrors.password} />
                <PasswordStrengthFeedback feedback={passwordStrengthFeedback} />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseAddUserModal}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-800 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                >
                  {loading ? "Creating..." : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUser && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
          <div
            ref={editUserModalRef}
            className="bg-white dark:bg-slate-800 rounded-lg p-6 w-full max-w-md shadow-xl border dark:border-slate-700 transition-colors duration-200"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit User
            </h3>
            <form onSubmit={handleEditUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={userForm.name}
                  onChange={(e) =>
                    setUserForm({ ...userForm, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) =>
                    setUserForm({ ...userForm, email: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Role
                </label>
                <select
                  value={userForm.role}
                  onChange={(e) =>
                    setUserForm({ ...userForm, role: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditUser(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-800 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 shadow-sm transition-all"
                >
                  {loading ? "Updating..." : "Update User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPassword && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
          <div
            ref={resetPasswordModalRef}
            className="bg-white dark:bg-slate-800 rounded-lg p-6 w-full max-w-md shadow-xl border dark:border-slate-700 transition-colors duration-200"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Reset Password
            </h3>
            <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">
              Reset password for{" "}
              <strong className="font-semibold text-blue-600 dark:text-blue-400">
                {selectedUser.name}
              </strong>
            </p>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showResetNewPassword ? "text" : "password"}
                    placeholder="New Password"
                    value={resetPasswordForm.newPassword}
                    onChange={(e) =>
                      handleResetPasswordFieldChange("newPassword", e.target.value)
                    }
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:border-transparent transition-colors duration-200 bg-white dark:bg-slate-900 text-black dark:text-white ${
                      resetPasswordErrors.newPassword
                        ? "border-red-500 dark:border-red-500 focus:ring-red-500"
                        : "border-gray-300 dark:border-slate-600 focus:ring-blue-500"
                    }`}
                    required
                  />
                  <button
                    type="button"
                    tabIndex="-1"
                    onClick={() => setShowResetNewPassword(!showResetNewPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 transition-colors"
                    title={showResetNewPassword ? "Hide password" : "Show password"}
                  >
                    {showResetNewPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <FieldError error={resetPasswordErrors.newPassword} />
                <PasswordStrengthFeedback feedback={resetPasswordStrengthFeedback} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showResetConfirmPassword ? "text" : "password"}
                    placeholder="Confirm Password"
                    value={resetPasswordForm.confirmPassword}
                    onChange={(e) =>
                      handleResetPasswordFieldChange("confirmPassword", e.target.value)
                    }
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:border-transparent transition-colors duration-200 bg-white dark:bg-slate-900 text-black dark:text-white ${
                      resetPasswordErrors.confirmPassword
                        ? "border-red-500 dark:border-red-500 focus:ring-red-500"
                        : "border-gray-300 dark:border-slate-600 focus:ring-blue-500"
                    }`}
                    required
                  />
                  <button
                    type="button"
                    tabIndex="-1"
                    onClick={() => setShowResetConfirmPassword(!showResetConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 transition-colors"
                    title={showResetConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showResetConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <FieldError error={resetPasswordErrors.confirmPassword} />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseResetPasswordModal}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-800 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 shadow-sm transition-all"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete / Deactivate Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setUserToDelete(null);
        }}
        onConfirm={confirmDeleteUser}
        title="Deactivate User"
        message={
          userToDelete
            ? `Are you sure you want to deactivate ${userToDelete.name}? They will no longer be able to log in until reactivated.`
            : "Are you sure you want to deactivate this user?"
        }
        confirmText="Deactivate User"
        cancelText="Cancel"
        type="danger"
        isLoading={loading}
      />

      {/* Activate Confirmation Modal */}
      <ConfirmationModal
        isOpen={showActivateConfirm}
        onClose={() => {
          setShowActivateConfirm(false);
          setUserToActivate(null);
        }}
        onConfirm={confirmActivateUser}
        title="Activate User"
        message={
          userToActivate
            ? `Are you sure you want to activate ${userToActivate.name}? They will regain full access and be able to log in.`
            : "Are you sure you want to activate this user?"
        }
        confirmText="Activate User"
        cancelText="Cancel"
        type="success"
        isLoading={loading}
      />
    </div>
  );
};

export default UserManagement;
